import { useState } from "react";

import { Select, TextField } from "@king-ict/eupisi-ui/components";
import { CodeBook } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import StaticField from "../../components/StaticField";
import { QualificationType } from "../../models/CodeBook";
import { ProgramTypeCodeBook } from "../../models/Program";
import { ProgramRequestData } from "../../models/ProgramRequest";
import { ProgramRequestDetail, ProgramRequestDetailBase } from "../../models/ProgramRequest/ProgramRequestDetail";
import {
  useGetEducationSectorsQuery,
  useGetEducationSubSectorsQuery,
  useGetQualificationTypesByProgramQuery
} from "../../queries/codeBookQuery";

interface Props {
  programTypeId: number;
  programRequestData?: ProgramRequestData;
  programRequestDetail?: ProgramRequestDetail;
  readonly?: boolean;
  onChange: (e: any) => void;  
  errors: any;
}

/**
 * Renders 3 rows of editable Program Name, Qualification Type, 
 * hkoLevel, and sector info.
 */
export function ExpertiseNewProgramInfo({
  programTypeId,
  programRequestData = undefined,
  programRequestDetail = undefined,
  readonly = false,  
  onChange,
  errors
}: Props) {
  const { t } = useTranslation();
  const { data: educationSectors } = useGetEducationSectorsQuery();

  const getEducationSectorId = (): number => {    
    if (programRequestData !== undefined) {
      return programRequestData!.educationSectorId!;
    }
    else {
      return programRequestDetail!.educationSectorId!;
    }
  }

  const { data: educationSubSectors } = useGetEducationSubSectorsQuery(getEducationSectorId(), { skip: !getEducationSectorId() });

  const { data: qualificationTypes } = useGetQualificationTypesByProgramQuery(programTypeId, {
    skip: !programTypeId,
  });
  const initHkoLevels = (): CodeBook[] => {
    var hkoLevel = [];

    if (programRequestData !== undefined && programRequestData.qualificationTypeId && programRequestData.qualificationTypeId > 0) {
      if (qualificationTypes) {
        for (let i = 0; i < qualificationTypes.length; i++) {
          if (qualificationTypes[i].id === programRequestData.qualificationTypeId) {
            hkoLevel.push(qualificationTypes[i].hkoLevel);
          }
        }
      }
    }

    else if (programRequestDetail !== undefined && programRequestDetail.qualificationTypeId && programRequestDetail.qualificationTypeId > 0) {
      if (qualificationTypes) {
        for (let i = 0; i < qualificationTypes.length; i++) {
          if (qualificationTypes[i].id === programRequestDetail.qualificationTypeId) {
            hkoLevel.push(qualificationTypes[i].hkoLevel);
          }
        }
      }
    }

    return hkoLevel;
  };

  const getRequestHkoLevelName = (): string => {
    let hkoLevelName = "";

    if (hkoLevels && programRequestData !== undefined && programRequestData.hkoLevelId) {
      var currentHkoLevel = hkoLevels.find(hko => hko.id === programRequestData.hkoLevelId);

      if (currentHkoLevel) {
        hkoLevelName = currentHkoLevel.name;
      }
    }

    else if (hkoLevels && programRequestDetail !== undefined && programRequestDetail.hkoLevelId) {
      var currentHkoLevel = hkoLevels.find(hko => hko.id === programRequestDetail.hkoLevelId);

      if (currentHkoLevel) {
        hkoLevelName = currentHkoLevel.name;
      }
    }

    return hkoLevelName;
  }
  const [hkoLevels, setHkoLevels] = useState<CodeBook[]>(initHkoLevels());
  const [hkoLevelName, setHkoLevelName] = useState(getRequestHkoLevelName());

  const getQualificationTypesSelectData = () => {
    if (!qualificationTypes) {
      return codeBookToSelectData(undefined);
    } else {
      var distinctQualificationTypes: CodeBook[] = [];

      for (let i = 0; i < qualificationTypes.length; i++) {
        if (!distinctQualificationTypes.find(qt => qt.id === qualificationTypes[i].id)) {
          distinctQualificationTypes.push(qualificationTypes[i]);
        }
      }

      return codeBookToSelectData(distinctQualificationTypes);
    }
  };

  const handleQualificationTypeChange = (event: any) => {
    const qualificationTypeId = event.target.value;
    var hkoLevel = [];

    if (qualificationTypes) {
      for (let i = 0; i < qualificationTypes.length; i++) {
        if (qualificationTypes[i].id === qualificationTypeId) {
          hkoLevel.push(qualificationTypes[i].hkoLevel);
        }
      }
    }    
    setHkoLevels(hkoLevel);

    var hkoLevelId = hkoLevel.length === 1 ? hkoLevel[0].id : 0;
    setHkoLevelName(hkoLevelId > 0 && hkoLevel.length === 1 ? hkoLevel[0].name : "");

    programRequestData!.qualificationTypeId = qualificationTypeId;
    programRequestData!.hkoLevelId = hkoLevelId;
    
    onChange(event);
  };

  return (
    <>
      <Row>
        <Col md={8}>
          <TextField
            label={t("request.programNameFreeEntry")}
            name={nameof<ProgramRequestData>("programName")}
            value={programRequestData?.programName ?? programRequestDetail?.programName}
            maxLength={512}
            onChange={onChange}
            errorText={errors?.programName}            
            plaintext={readonly}
          />
        </Col>
      </Row>
      <Row>
        <Col md={4}>
          {readonly &&
            <StaticField
              label={t("request.qualification")}
              value={programRequestDetail!.qualificationType}
            />
          }
          {!readonly &&
            <Select
              label={t("request.qualification")}
              name={nameof<ProgramRequestData>("qualificationTypeId")}
              value={programRequestData!.qualificationTypeId}
              errorText={errors?.qualificationTypeId}              
              selectData={getQualificationTypesSelectData()}
              onChange={handleQualificationTypeChange}
            />
          }
        </Col>
        {readonly && programRequestDetail &&
          <Col md={4}>
            <StaticField
              label={t("request.hkoLevel")}
              value={programRequestDetail!.hkoLevel}
            />
          </Col>
          ||
          !readonly && hkoLevels && hkoLevels.length > 2 &&
          <Col md={4}>
            <Select
              label={t("request.hkoLevel")}
              name={nameof<ProgramRequestData>("hkoLevelId")}
              value={programRequestData!.hkoLevelId}
              onChange={onChange}
              selectData={codeBookToSelectData(hkoLevels)}
              errorText={errors?.hkoLevelId}              
              plaintext={readonly}
            />
          </Col>
          ||
          (!hkoLevels || hkoLevels.length < 2) &&
          <Col md={4}>
            <StaticField
              label={t("request.hkoLevel")}
              value={hkoLevelName}
            />
          </Col>
        }
      </Row>
      <Row>
        <Col md={4}>
          {readonly &&
            <StaticField
              label={t("common.educationSector")}
              value={programRequestDetail!.educationSector}
            />
          }
          {!readonly &&
            <Select
              label={t("common.educationSector")}
              name={nameof<ProgramRequestData>("educationSectorId")}
              value={programRequestData!.educationSectorId}
              onChange={onChange}
              selectData={codeBookToSelectData(educationSectors)}
              plaintext={readonly}
            />
          }
        </Col>
        {readonly && programRequestDetail !== undefined && programRequestDetail!.qualificationTypeId !== QualificationType.MicroQualification &&
          <Col md={4}>
            <StaticField
              label={t("common.educationSubSector")}
              value={programRequestDetail!.educationSubSector}
            />
          </Col>
        }
        {!readonly && programRequestData !== undefined && programRequestData!.qualificationTypeId !== QualificationType.MicroQualification &&
          <Col md={4}>
            <Select
              label={t("common.educationSubSector")}
              name={nameof<ProgramRequestData>("educationSubSectorId")}
              value={programRequestData!.educationSubSectorId}
              onChange={onChange}
              selectData={codeBookToSelectData(educationSubSectors)}
              errorText={errors?.educationSubSectorId}
              plaintext={readonly}
            />
          </Col>
        }
      </Row>
    </>
  )
}